// FT-186: нативная камера (@capacitor/camera 8.2) в фото выдачи/приёма приложений.
//
// Камера только поставляет файлы: дальше они идут в тот же экземпляр Uppy и прежним путём
// (загрузка частями, подтверждение, лимиты FT-152/153). Плагин, хранилище, fetch и отчёт —
// параметрами: спеки гоняют сервис без Capacitor (тот же приём, что appReviewService.js).
import Vue from "vue";
import { Camera, CameraResultType, CameraSource, EncodingType, MediaTypeSelection } from "@capacitor/camera";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { storeRestoredDamageControl, resetRestoredDamageControl } from "@/services/damageControlRecovery";

export const PENDING_KEY = "ft186.pendingCapture";
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000;
// Отмена съёмки и отмена выбора в галерее — решение человека, не сбой (CTR-03).
export const CANCEL_CODES = ["OS-PLUG-CAMR-0006", "OS-PLUG-CAMR-0020", "OS-PLUG-CAMR-0017"];
// Устаревший getPhoto (Android) отклоняет отмену без кода — этой строкой (LegacyCameraFlow.java).
const LEGACY_CANCEL_MESSAGE = "User cancelled photos app";

// REQ-02: кадр уменьшается и перекодируется на телефоне. quality 80 — политика компрессора
// фото выдачи (uppy_checkin), 1920 — его же потолок по длинной стороне.
const FRAME = { quality: 80, targetWidth: 1920, targetHeight: 1920, correctOrientation: true };
export const TAKE_PHOTO_OPTIONS = { ...FRAME, encodingType: EncodingType.JPEG, saveToGallery: false };
// Android снимает устаревшим getPhoto: только он сохраняет вызов через смерть процесса
// (LegacyCameraFlow + onSaveInstanceState) и отдаёт снимок событием appRestoredResult.
// takePhoto/chooseFromGallery (IonCameraFlow) держат вызов в памяти — после убийства
// приложения результат теряется (ASM-01, решение USER 2026-10-04).
export const GET_PHOTO_OPTIONS = {
  quality: FRAME.quality,
  width: FRAME.targetWidth,
  height: FRAME.targetHeight,
  correctOrientation: true,
  resultType: CameraResultType.Uri,
  source: CameraSource.Camera,
  saveToGallery: false,
};
export function galleryOptions(limit) {
  return { ...FRAME, mediaType: MediaTypeSelection.Photo, allowMultipleSelection: true, limit };
}

const TYPES = {
  jpeg: ["image/jpeg", "jpg"],
  jpg: ["image/jpeg", "jpg"],
  png: ["image/png", "png"],
  heic: ["image/heic", "heic"],
  heif: ["image/heif", "heif"],
  tif: ["image/tiff", "tiff"],
  tiff: ["image/tiff", "tiff"],
  mov: ["video/quicktime", "mov"],
  quicktime: ["video/quicktime", "mov"],
  mp4: ["video/mp4", "mp4"],
  m4v: ["video/mp4", "mp4"],
};

export const defaultStorage = {
  async get(key) {
    const { value } = await Preferences.get({ key });
    return value === undefined ? null : value;
  },
  set: (key, value) => Preferences.set({ key, value }),
  remove: (key) => Preferences.remove({ key }),
};

function defaultReport(text, error, custom) {
  const rollbar = Vue.prototype.$rollbar;
  if (rollbar && typeof rollbar.error === "function") rollbar.error(text, error, custom);
  else console.warn(text, error, custom);
}

let brokenInSession = false;
export function markBrokenInSession() { brokenInSession = true; }
export function isBrokenInSession() { return brokenInSession; }

export function isNativeCameraEnabled({ platform, flags, broken }) {
  if (platform !== "ios" && platform !== "android") return false;
  if (broken) return false;
  return Boolean(flags && flags[platform] === true);
}

export function isCancel(error) {
  return Boolean(error && (CANCEL_CODES.includes(error.code) || error.message === LEGACY_CANCEL_MESSAGE));
}

function typeFromExtension(path) {
  const match = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(String(path || ""));
  return match ? TYPES[match[1].toLowerCase()] : undefined;
}

function typeFromMime(mime) {
  const entry = Object.values(TYPES).find(([type]) => type === mime);
  return entry || undefined;
}

// Тип файла — из того, что плагин действительно отдаёт: Photo (getPhoto) несёт format,
// MediaResult (takePhoto/chooseFromGallery) — нет, только путь и, по запросу, metadata.format.
// Признак «уже сжат» (nativeCompressed) — только если плагин сжимал (compressed: съёмка камерой)
// и тип уверенно JPEG. Галерея в 8.2.2 quality/targetWidth не применяет (iOS их не читает,
// Android не передаёт в обработку): её файлы, как и HEIC (OQ-02), идут в компрессор Uppy.
export async function mediaResultToFile(result, { fetchImpl = fetch, now = Date.now, index = 0, compressed = false, video = false } = {}) {
  const path = result && result.webPath;
  if (!path) throw Object.assign(new Error("native camera: media without webPath"), { code: "FT186-NO-PATH" });
  const blob = await (await fetchImpl(path)).blob();
  const format = result.format || (result.metadata && result.metadata.format);
  const known = (format && TYPES[String(format).toLowerCase()])
    || typeFromExtension(path)
    || typeFromExtension(result.uri)
    || typeFromExtension(result.path)
    || typeFromMime(blob.type);
  const [type, extension] = known || (video ? TYPES.mov : TYPES.jpeg);
  const name = video ? `video-${now()}.${extension}` : `photo-${now()}-${index}.${extension}`;
  const file = new File([blob], name, { type });
  Object.defineProperty(file, "nativeCompressed", { value: compressed && Boolean(known) && type === "image/jpeg" });
  return file;
}

export async function capturePhotos({ source, bookingId, limit }, deps = {}) {
  const camera = deps.camera || Camera;
  const storage = deps.storage || defaultStorage;
  const platform = deps.platform || Capacitor.getPlatform();
  const now = deps.now || Date.now;

  // «Ожидание» пишется ДО вызова: если Android убьёт приложение, пока открыта камера,
  // только по нему восстановление узнает бронь (REQ-04, CTR-02).
  await storage.set(PENDING_KEY, JSON.stringify({ bookingId, source, at: new Date(now()).toISOString() }));
  try {
    let results;
    if (source === "gallery") results = (await camera.chooseFromGallery(galleryOptions(limit))).results;
    else if (platform === "android") results = [await camera.getPhoto(GET_PHOTO_OPTIONS)];
    else results = [await camera.takePhoto(TAKE_PHOTO_OPTIONS)];
    const compressed = source !== "gallery";
    return await Promise.all(results.map((result, index) => mediaResultToFile(result, { fetchImpl: deps.fetchImpl, now, index, compressed })));
  } catch (error) {
    if (isCancel(error)) return [];
    throw error;
  } finally {
    await Promise.resolve(storage.remove(PENDING_KEY)).catch(() => {});
  }
}

export async function captureVideo({ limit }, deps = {}) {
  const camera = deps.camera || Camera;
  try {
    const result = await camera.recordVideo({ saveToGallery: false, isPersistent: false, includeMetadata: true });
    // Метаданные могут отсутствовать. Тогда размер проверит Uppy после чтения файла.
    if (result.metadata && result.metadata.size > limit) {
      throw Object.assign(new Error("native camera: video exceeds upload limit"), { code: "FT210-TOO-LARGE" });
    }
    return await mediaResultToFile(result, { fetchImpl: deps.fetchImpl, now: deps.now, video: true });
  } catch (error) {
    if (isCancel(error)) return null;
    throw error;
  }
}

export function reportFailure(error, { platform, method }, report = defaultReport) {
  report("native_camera_failed", error, { platform, code: error && error.code, method });
}

// REQ-04 (Android). Если система убила приложение, пока открыта камера, Capacitor на
// холодном старте отдаёт результат getPhoto событием appRestoredResult (@capacitor/app):
// { pluginId, methodName, success, data, error }. Слушатель ставится в main.js ДО Vue —
// событие удерживается до первого подписчика (как appUrlOpen). Выбор из галереи
// (chooseFromGallery) такого события не даёт — после убийства его выбор теряется.
let restored = null; // { bookingId: number, results: Photo[] }

const RESTORABLE = {
  getPhoto: (data) => (data && data.webPath ? [data] : null),
};

export function parseRestoredEvent(event) {
  if (!event || event.pluginId !== "Camera" || !event.success) return null;
  const parse = RESTORABLE[event.methodName];
  return parse ? parse(event.data) : null;
}

export async function handleRestoredEvent(event, { storage = defaultStorage, now = Date.now, router } = {}) {
  const raw = await Promise.resolve(storage.get(PENDING_KEY)).catch(() => null);
  await Promise.resolve(storage.remove(PENDING_KEY)).catch(() => {});
  if (!raw) return null;

  let pending;
  try { pending = JSON.parse(raw); } catch (e) { return null; }
  if (pending && pending.kind === "damage_control") {
    const data = event && event.pluginId === "Camera" && event.methodName === "getPhoto" && event.success
      ? event.data
      : null;
    if (!(now() - Date.parse(pending.at) <= PENDING_TTL_MS) || !(data && (data.webPath || data.path))) return null;
    return storeRestoredDamageControl(pending, data);
  }
  const results = parseRestoredEvent(event);
  if (!results) return null;
  const bookingId = Number(pending && pending.bookingId);
  if (!bookingId || !(now() - Date.parse(pending.at) <= PENDING_TTL_MS)) return null;

  restored = { bookingId, results };
  // До входа роутер уведёт на /signin?from=… и вернёт сюда после него (authRedirect.js).
  if (router) router.onReady(() => router.push(`/bookings/${bookingId}`).catch(() => {}));
  return restored;
}

export function hasRestoredCaptureFor(bookingId) {
  return Boolean(restored && restored.bookingId === Number(bookingId));
}

// Одноразово и только своей брони (FM-02): в чужую бронь снимок не попадёт.
export async function takeRestoredCapture(bookingId, deps = {}) {
  if (!hasRestoredCaptureFor(bookingId)) return [];
  const { results } = restored;
  restored = null;
  // Восстанавливается только getPhoto — снимок камеры, сжатый на телефоне.
  return Promise.all(results.map((result, index) => mediaResultToFile(result, { fetchImpl: deps.fetchImpl, now: deps.now, index, compressed: true })));
}

export function installRestoreListener(capacitorApp, router, deps = {}) {
  return capacitorApp.addListener("appRestoredResult", (event) => {
    handleRestoredEvent(event, { ...deps, router }).catch((error) => (deps.report || defaultReport)("native_camera_restore_failed", error));
  });
}

export function resetNativeCameraState() {
  brokenInSession = false;
  restored = null;
  resetRestoredDamageControl();
}
